import React from 'react';

import CliveCopyToClipboardButton from '~/components/Clive/CliveCopyToClipboardButton';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { Text } from '~/components/core/TextComponents';

import { buildResponseExample, simplifyResponseSchema } from './schemaSummary';
import TEXTS from './texts';
import type { AvailableTool } from './types';

// Read-only view of a tool's code-defined response contract. The default view is an example of the
// response payload — each property shown with its type, with the pydantic `$defs`/`$ref`/internal
// type name resolved away — since that is what an operator needs to see. The full raw JSON Schema is
// an opt-in advanced view: it lives behind a collapsed "View JSON Schema" section, with a copy button
// for whoever actually implements the endpoint. Owned by FiveSigma — no edit affordance.
const ResponseSchemaPanel: React.FC<{ tool: AvailableTool }> = ({ tool }) => {
  if (!tool.response_json_schema) return null;

  const simplified = simplifyResponseSchema(tool.response_json_schema);
  const example = simplified !== null ? buildResponseExample(simplified) : null;
  const prettySchema = JSON.stringify(tool.response_json_schema, null, 2);
  const version = tool.response_contract_version;

  return (
    <div className="flex flex-col gap-16">
      <div className="flex items-start justify-between gap-8">
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.responseSchema.ownedBy}
          {version ? ` (${TEXTS.responseSchema.version(version)})` : ''}
        </Text>
        <CliveCopyToClipboardButton textToCopy={prettySchema} ariaLabel={TEXTS.responseSchema.copy} />
      </div>

      {simplified ? (
        <>
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.responseSchema.exampleHelper}
          </Text>
          <pre className="font-mono max-h-60 m-0 overflow-auto rounded-xl border border-slate-300 bg-slate-100 p-16 text-xs">
            {JSON.stringify(example, null, 2)}
          </pre>
          <CollapsibleWrapper
            title={TEXTS.responseSchema.viewJsonSchema}
            variant={CollapsibleWrapper.VARIANT.slim}
            chevronContainerClassName="hidden"
          >
            <pre className="font-mono max-h-60 m-0 overflow-auto rounded-xl border border-slate-300 bg-slate-100 p-16 text-xs">
              {prettySchema}
            </pre>
          </CollapsibleWrapper>
        </>
      ) : (
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.responseSchema.freeForm}
        </Text>
      )}

      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.responseSchema.copyHelper}
      </Text>
    </div>
  );
};

export default ResponseSchemaPanel;
